import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, cents } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { track } from "../../lib/analytics";
import { bySlug } from "../../content/articles";

const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

export function Inline({ text }) {
  const parts = [];
  let last = 0, m, i = 0;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const [, label, url] = m;
    parts.push(url.startsWith("/")
      ? <Link key={i++} to={url} className="underline decoration-[#A35847]/50 hover:decoration-[#A35847]">{label}</Link>
      : <a key={i++} href={url} target="_blank" rel="noopener noreferrer" className="underline">{label}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export function Blocks({ blocks }) {
  return (
    <>
      {blocks.map((b, i) => {
        if (b.h2) return <h2 key={i} id={slugify(b.h2)} className="text-xl md:text-2xl font-medium mt-10 mb-3">{b.h2}</h2>;
        if (b.h3) return <h3 key={i} className="text-lg font-medium mt-6 mb-2">{b.h3}</h3>;
        if (b.p) return <p key={i} className="text-[15px] leading-relaxed text-[#171715]/85 mb-4"><Inline text={b.p} /></p>;
        if (b.ul) return <ul key={i} className="list-disc pl-6 space-y-1.5 text-[15px] text-[#171715]/85 mb-4">{b.ul.map((li, j) => <li key={j}><Inline text={li} /></li>)}</ul>;
        if (b.table) return (
          <div key={i} className="overflow-x-auto mb-5">
            <table className="w-full text-sm border border-[#EBD9D0]">
              <thead><tr className="bg-[#F3EFE7] text-left">{b.table.headers.map((h, j) => <th key={j} className="px-3 py-2 font-medium border-b border-[#EBD9D0]">{h}</th>)}</tr></thead>
              <tbody>{b.table.rows.map((r, j) => <tr key={j} className="border-b border-[#EBD9D0]/60 align-top">{r.map((c, k) => <td key={k} className="px-3 py-2"><Inline text={String(c)} /></td>)}</tr>)}</tbody>
            </table>
          </div>
        );
        return null;
      })}
    </>
  );
}

let _productsPromise = null;
const loadProducts = () => (_productsPromise ||= api.get("/products").then((r) => r.data).catch(() => []));

export function ProductCards({ slugs, articleSlug }) {
  const [prods, setProds] = useState([]);
  const { items, setQty } = useCart();
  useEffect(() => { loadProducts().then((all) => setProds(slugs.map((s) => all.find((p) => p.slug === s)).filter(Boolean))); }, [slugs]);
  if (!prods.length) return null;
  return (
    <aside className="my-8 border-y border-[#EBD9D0] py-6" data-testid="article-product-cards" aria-label="Juices mentioned in this guide">
      <p className="eyebrow mb-4">From the menu</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {prods.map((p) => (
          <div key={p.slug} className="card-paper p-4 flex gap-3" data-testid={`article-product-card-${p.slug}`}>
            <span className="photo-panel p-1.5 self-start shrink-0 flex items-center justify-center"><img src={p.image} alt={p.image_alt} width="48" height="64" loading="lazy" className="h-16 w-auto object-contain" /></span>
            <div className="min-w-0">
              <p className="font-medium text-sm">{p.name}</p>
              <p className="text-xs text-[#171715]/65 mt-0.5">{p.ingredients.join(", ")}{p.functional_addition ? ` · ${p.functional_addition}` : ""}</p>
              <p className="text-xs font-mono mt-1">{cents(p.price_cents)} <span className="text-[#171715]/55">· posted reference price</span></p>
              <div className="flex flex-wrap gap-2 mt-2">
                <button data-testid={`article-add-${p.slug}`} className="btn-ink text-xs px-3 py-1.5" onClick={() => { setQty(p.slug, (items[p.slug] || 0) + 1); toast.success(`${p.name} added to your stack`); track("add_to_cart", { item: p.slug, article_slug: articleSlug }); }}>Add to stack</button>
                <Link data-testid={`article-view-${p.slug}`} to={`/products/${p.slug}`} className="btn-ghost text-xs px-3 py-1.5">View product</Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

export function LeadForm({ articleSlug }) {
  const [form, setForm] = useState({ email: "", name: "", consent: false, website: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (state === "busy" || state === "done") return;
    if (form.website) { setState("done"); return; } // honeypot: pretend success, send nothing
    if (!/.+@.+\..+/.test(form.email)) { setError("Enter a valid email address."); return; }
    setError(""); setState("busy");
    try {
      await api.post("/leads", { email: form.email, name: form.name, source: `journal:${articleSlug}`, marketing_opt_in: form.consent });
      track("journal_lead_submit", { article_slug: articleSlug, source: "journal" });
      setState("done");
    } catch (err) { setError(errMsg(err)); setState("idle"); }
  };
  return (
    <section className="card-paper p-6 my-10" data-testid="article-lead-form">
      <h2 className="text-lg font-medium mb-1">Get Dallas pickup updates</h2>
      <p className="text-xs text-[#171715]/65 mb-4">New flavors, pickup locations and plain-language guides. No spam, unsubscribe anytime.</p>
      {state === "done" ? (
        <p className="text-sm text-[#A35847] font-medium" data-testid="article-lead-success" role="status">Thanks — you're on the list. Check your inbox for a welcome note if you opted into emails.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3" noValidate>
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" aria-hidden="true" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor={`lead-name-${articleSlug}`} className="text-xs font-medium block mb-1">Name (optional)</label>
              <input id={`lead-name-${articleSlug}`} data-testid="article-lead-name" className="input-line w-full" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor={`lead-email-${articleSlug}`} className="text-xs font-medium block mb-1">Email</label>
              <input id={`lead-email-${articleSlug}`} data-testid="article-lead-email" type="email" required className="input-line w-full" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-[#171715]/75">
            <input data-testid="article-lead-consent" type="checkbox" className="mt-0.5 h-4 w-4" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
            <span>Email me updates and guides (optional). See our <Link to="/privacy" className="underline">privacy policy</Link>.</span>
          </label>
          {error && <p role="alert" data-testid="article-lead-error" className="text-xs text-[#9c3b2a]">{error}</p>}
          <button data-testid="article-lead-submit" className="btn-ink text-sm" disabled={state === "busy"}>{state === "busy" ? "Sending…" : "Keep me posted"}</button>
        </form>
      )}
    </section>
  );
}

export function RelatedCards({ slugs }) {
  const rel = slugs.map((s) => bySlug[s]).filter(Boolean).slice(0, 3);
  if (!rel.length) return null;
  return (
    <section className="my-10" data-testid="article-related-reading">
      <h2 className="text-xl font-medium mb-4">Related reading</h2>
      <div className="grid sm:grid-cols-3 gap-4">
        {rel.map((a) => (
          <Link key={a.slug} to={`/journal/${a.slug}`} className="card-paper p-4 block hover:shadow-md transition-shadow" data-testid={`related-card-${a.slug}`}>
            <p className="eyebrow mb-1">{a.section}</p>
            <p className="font-medium text-sm leading-snug">{a.title}</p>
            <p className="text-xs text-[#171715]/65 mt-1 line-clamp-3">{a.metaDescription}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
